import { replaceContainer as replaceComponentContainer } from 'utils/svelte/replace-container';
import type { Component } from 'types/component';
import type { SvelteComponent } from 'types/svelte';
import { ObjectUtil } from '@chesscom/web-ui';

const { pick } = ObjectUtil;

/**
 * Create a svelte component that adheres to a Component interface
 */
export function createSvelteComponent<API extends Component.API>({
  ComponentType,
  methods,
  options,
  replaceContainer = true,
}: {
  // https://chesscom.atlassian.net/browse/CV-381279
  ComponentType: SvelteComponent.Initializer | any;
  methods?: string[];
  options: SvelteComponent.Options;
  replaceContainer?: boolean;
}): API {
  const instance = replaceContainer
    ? replaceComponentContainer(ComponentType, options)
    : new ComponentType(options);

  const api = <API>{
    destroy,
    el: options.target,
    get,
    instance,
    on,
    set,
  };

  // expose any api methods on the component
  if (methods && methods.length) {
    methods.forEach((k) => {
      api[<keyof API>k] = <API[keyof API]>(<unknown>instance[<keyof SvelteComponent.API>k]);
    });
  }

  return api;

  /**
   * Destroy the instance.
   */
  function destroy(): void {
    instance.$destroy();
  }

  /**
   * Get data from the component.
   */
  function get(props: string | string[]): any {
    if (typeof props === 'string') {
      return (<any>instance).$$.ctx[props];
    }

    return pick((<any>instance).$$.ctx, props);
  }

  /**
   * Register an event listener on the component.
   */
  function on(event: string, callback: (event: CustomEvent) => void): void {
    instance.$on(event, callback);
  }

  /**
   * Set the data of a component.
   */
  function set(data: Record<string, any>): void {
    instance.$set(data);
  }
}
